// The pricing scope: a built-in checklist whose answers are the pricing model's
// inputs.
//
// It is a checklist like any other as far as the panel and the extraction
// pipeline are concerned - the same ChecklistField shape, the same descriptions
// doing the same work. What is different is that its fields are fixed, because
// they are the model's columns, and that the answers add up to a price rather
// than just a filled-in list.
//
// Two deliberate departures from the model's own form:
//
//   The company is never extracted. The model needs it and a call will not
//   reliably say it, so it is picked by hand and the estimate stays hidden
//   until it has been. It is the one required answer.
//
//   Rooms are one field, not twenty. The model takes a count per room type, and
//   putting twenty rows in a panel meant to be read at a glance mid-call would
//   wreck it. One field is extracted as a list and parsed into counts.

import type { ChecklistField } from '../config/types';
import { SPEC, estimate, type Estimate, type ScopeInput } from './score';

/**
 * What marks the row in checklist_configs as this checklist.
 *
 * Identity is the builtin_key, never the id. The row is an ordinary one with an
 * ordinary numeric id - the app supplies its fields, but the database owns the
 * row so it can be selected and recorded like any other checklist. An earlier
 * draft of this used a sentinel id, which nothing ever has, so nothing was ever
 * recognised as the pricing scope and the placeholder price stood.
 */
export const PRICING_BUILTIN_KEY = 'pricing';

export const PRICING_CONFIG_NAME = 'Pricing scope';

/** Field ids, which are also how answers are keyed everywhere else. */
export const FIELD = {
  org: 'org_id',
  projectType: 'project_type',
  category: 'category_of_loss',
  sqft: 'total_sqft',
  rooms: 'rooms',
  nonRoomSections: 'non_room_sections',
  containment: 'flag_containment',
  asbestos: 'flag_asbestos',
} as const;

/** The company. Picked, never extracted - see the note at the top. */
export const ORGS: { id: string; label: string }[] = [
  { id: 'HDR', label: 'HydroDry Restoration' },
  { id: 'JDR', label: 'JDR Restoration' },
  { id: 'MDR', label: 'Mason Dixon Restoration' },
  { id: 'RBG', label: 'Renew By Grewe' },
  { id: 'LVN', label: 'Longview: Nashville' },
];

export const ROOMS: { column: string; label: string; spoken: string[] }[] = [
  { column: 'room_attic', label: 'Attic', spoken: ['attic'] },
  { column: 'room_basement', label: 'Basement', spoken: ['basement', 'cellar'] },
  { column: 'room_bathroom', label: 'Bathroom', spoken: ['bathroom', 'bath', 'powder room', 'ensuite'] },
  { column: 'room_bedroom', label: 'Bedroom', spoken: ['bedroom', 'bed room', 'master'] },
  { column: 'room_closet', label: 'Closet', spoken: ['closet', 'wardrobe'] },
  { column: 'room_crawlspace', label: 'Crawlspace', spoken: ['crawlspace', 'crawl space'] },
  { column: 'room_dining_room', label: 'Dining room', spoken: ['dining room', 'dining'] },
  { column: 'room_entry', label: 'Entry', spoken: ['entry', 'entryway', 'foyer'] },
  { column: 'room_garage', label: 'Garage', spoken: ['garage'] },
  { column: 'room_hallway', label: 'Hallway', spoken: ['hallway', 'hall', 'corridor'] },
  { column: 'room_kitchen', label: 'Kitchen', spoken: ['kitchen'] },
  { column: 'room_laundry_room', label: 'Laundry room', spoken: ['laundry room', 'laundry', 'utility sink'] },
  { column: 'room_living_room', label: 'Living room', spoken: ['living room', 'lounge', 'family room', 'den'] },
  { column: 'room_mudroom', label: 'Mudroom', spoken: ['mudroom', 'mud room'] },
  { column: 'room_office', label: 'Office', spoken: ['office', 'study'] },
  { column: 'room_other_room', label: 'Other room', spoken: ['other room', 'other'] },
  { column: 'room_recreation_room', label: 'Recreation room', spoken: ['recreation room', 'rec room', 'game room'] },
  { column: 'room_stairway', label: 'Stairway', spoken: ['stairway', 'stairs', 'staircase'] },
  { column: 'room_storage', label: 'Storage', spoken: ['storage', 'store room'] },
  { column: 'room_utility_room', label: 'Utility room', spoken: ['utility room', 'utility', 'mechanical room'] },
];

/** Water category, as the model spells it. */
const CATEGORIES: { value: string; label: string }[] = [
  { value: '-1', label: 'Not a water loss' },
  { value: '1', label: 'Category 1 - clean water' },
  { value: '2', label: 'Category 2 - grey water' },
  { value: '3', label: 'Category 3 - black water' },
];

/** Every project type the model knows, kept and infrequent alike. */
function projectTypes(): string[] {
  const c = SPEC.categorical.find((entry) => entry.column === 'project_type');
  return [...(c?.kept_levels ?? []), ...(c?.infrequent_levels ?? [])].sort();
}

/**
 * The figure the model predicts. RCV is the estimate before depreciation and is
 * what an inspection is scoping toward, so it is fixed rather than asked about
 * - a live call is not the place to choose between accounting bases.
 */
export const TOTAL_AMOUNT_KIND = 'rcv';

/**
 * The checklist the panel renders and the extractor reads.
 *
 * Descriptions are written for the extractor, not for a person. The model
 * cannot see this file, only these sentences, so anything it needs to know
 * about how a thing gets said on a call has to be in them.
 */
export const PRICING_FIELDS: ChecklistField[] = [
  {
    id: FIELD.org,
    label: 'Company',
    description:
      'Which Longview company is doing the work. This is chosen by the inspector, not taken from the call.',
    type: 'enum',
    enumValues: ORGS.map((o) => o.id),
  },
  {
    id: FIELD.projectType,
    label: 'Project type',
    description:
      'The kind of job this is. Water and Water Mitigation are the common ones; use Fire, Mold, Sewage, Asbestos, Contents or Reconstruction when the call is clearly about that instead. Mitigation is the drying and removal work, Reconstruction is putting the building back. If the caller describes water damage without saying more, "Water Mitigation" is the answer.',
    type: 'enum',
    enumValues: projectTypes(),
  },
  {
    id: FIELD.category,
    label: 'Water category',
    description:
      'The IICRC category of the water, if this is a water loss. 1 is clean water from a supply line, a burst pipe or an overflowing tub. 2 is grey water: washing machines, dishwashers, toilet overflow with no solids. 3 is black water: sewage, a backed-up drain, ground water or flooding from outside. Answer -1 when the call is not about water at all. Do not guess a number from the word "flood" alone - flooding from a river is 3, an overflowing bath is 1.',
    type: 'enum',
    enumValues: CATEGORIES.map((c) => c.value),
  },
  {
    id: FIELD.sqft,
    label: 'Affected square footage',
    description:
      'How many square feet are affected, as a number. Only what is damaged or being worked on, not the size of the whole house. Leave empty if nobody says - a guess here moves the price.',
    type: 'number',
  },
  {
    id: FIELD.rooms,
    label: 'Rooms affected',
    description:
      'Which rooms are affected and how many of each, as a comma-separated list of "room x count", for example "bedroom x2, bathroom x1, hallway x1". Use these room names only: ' +
      ROOMS.map((r) => r.label.toLowerCase()).join(', ') +
      '. Count each room separately - two bedrooms is "bedroom x2". Put anything that does not fit under "other room".',
    type: 'string',
  },
  {
    id: FIELD.nonRoomSections,
    label: 'Job-wide sections',
    description:
      'How many areas of work are not a specific room - whole-floor work, exterior, crawl space access, or general site-wide items. A number. Most jobs are 1. Leave empty if the call gives no sense of it.',
    type: 'number',
  },
  {
    id: FIELD.containment,
    label: 'Containment needed',
    description:
      'Whether containment is needed: sealing the work area off with plastic sheeting and negative air, to stop spread to the rest of the building. Answer true or false only if the call actually settles it. Leave empty when it is not discussed - "not established" is a real answer here and the price accounts for it.',
    type: 'boolean',
  },
  {
    id: FIELD.asbestos,
    label: 'Asbestos',
    description:
      'Whether asbestos testing or abatement is involved. Older buildings, popcorn ceilings, floor tile and pipe insulation are the usual reasons it comes up. Answer true or false only if the call settles it. Leave empty when it is not discussed.',
    type: 'boolean',
  },
];

export function isPricingConfig(
  config: { builtinKey?: string | null } | null | undefined,
): boolean {
  return config?.builtinKey === PRICING_BUILTIN_KEY;
}

/**
 * "bedroom x2, bathroom, 3 hallways" -> counts per model column.
 *
 * Forgiving on purpose. The extractor is asked for one shape, and mostly
 * obliges, but this is speech-derived text arriving through a model - so a
 * count before or after the name, plurals, and stray words all have to survive.
 * Anything unrecognised is dropped rather than guessed at: a room the model
 * does not have a column for cannot be priced, and silently mapping it to
 * "other room" would be an invention.
 */
export function parseRooms(value: string | null | undefined): Record<string, number> {
  const counts: Record<string, number> = {};
  if (!value) return counts;

  for (const piece of value.split(/[,;\n]+/)) {
    const text = piece.trim().toLowerCase();
    if (!text) continue;

    // "x2", "× 2", "2" before or after the name.
    const after = text.match(/[x×]\s*(\d+)\s*$/);
    const before = text.match(/^(\d+)\s*[x×]?\s+/);
    const count = Number(after?.[1] ?? before?.[1] ?? 1);
    const name = text
      .replace(/[x×]\s*\d+\s*$/, '')
      .replace(/^\d+\s*[x×]?\s+/, '')
      .replace(/\(.*?\)/g, '')
      .trim()
      .replace(/s$/, '');

    if (!name) continue;

    // Longest spoken form first, so "living room" is not claimed by "room".
    const match = ROOMS.flatMap((room) => room.spoken.map((spoken) => ({ room, spoken })))
      .sort((a, b) => b.spoken.length - a.spoken.length)
      .find(({ spoken }) => name === spoken || name === `${spoken}s` || name.includes(spoken));

    if (!match) continue;
    counts[match.room.column] = (counts[match.room.column] ?? 0) + (Number.isFinite(count) ? count : 1);
  }

  return counts;
}

/** How the panel shows a parsed room list back. */
export function formatRooms(counts: Record<string, number>): string {
  return ROOMS.filter((room) => (counts[room.column] ?? 0) > 0)
    .map((room) => {
      const n = counts[room.column]!;
      return n > 1 ? `${room.label} ×${n}` : room.label;
    })
    .join(', ');
}

/** A tri-state boolean as the model wants it: 1, 0, or null for "not stated". */
function flag(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const text = String(value).trim().toLowerCase();
  if (['true', 'yes', '1', 'y'].includes(text)) return 1;
  if (['false', 'no', '0', 'n'].includes(text)) return 0;
  return null;
}

function numberOrNull(value: string | null | undefined): number | null {
  if (value == null || String(value).trim() === '') return null;
  // Strips "sq ft", commas, and anything else that rides along with a spoken number.
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Checklist answers -> model input.
 *
 * `answers` is keyed by field id, holding whatever the panel currently shows -
 * extracted, confirmed or typed, it makes no difference here.
 */
export function toScopeInput(answers: Record<string, string | null>): ScopeInput {
  const input: ScopeInput = {
    org_id: answers[FIELD.org] || null,
    project_type: answers[FIELD.projectType] || null,
    category_of_loss: answers[FIELD.category] || null,
    total_amount_kind: TOTAL_AMOUNT_KIND,
    total_sqft: numberOrNull(answers[FIELD.sqft]),
    // Zero rather than null: the model has no missing indicator for this one,
    // so a null would be imputed to the median and quietly invent work.
    non_room_sections: numberOrNull(answers[FIELD.nonRoomSections]) ?? 0,
    flag_containment: flag(answers[FIELD.containment]),
    flag_asbestos: flag(answers[FIELD.asbestos]),
  };

  const rooms = parseRooms(answers[FIELD.rooms]);
  for (const room of ROOMS) input[room.column] = rooms[room.column] ?? 0;

  return input;
}

/**
 * The estimate for a set of answers, or null when it cannot be priced.
 *
 * Only the company is required. Everything else has either a level the model
 * knows or an explicit "not stated" it was trained on, which is the point of
 * pricing from a call that is still in progress: the number exists from the
 * moment the company is chosen and sharpens as the call fills in.
 */
export function priceFor(answers: Record<string, string | null>): Estimate | null {
  if (!answers[FIELD.org]) return null;
  return estimate(toScopeInput(answers));
}

export const ORG_LABELS: Record<string, string> = Object.fromEntries(
  ORGS.map((o) => [o.id, o.label]),
);

export const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label]),
);
